<template>
  <component
    :is="props.tag"
    :href="props.tag === 'a' ? 'javascript:void(0)' : undefined"
    :class="`share-network-${key}`"
    @click="onClick"
  >
    <slot />
  </component>
  <a href="" />
</template>

<script lang="ts" setup>
import AvailableNetworks from '@/utils/share-networks'

type NetworkKeys = keyof typeof AvailableNetworks
type NetworkLowerKeys = Lowercase<NetworkKeys>

const props = withDefaults(
  defineProps<{
    network: NetworkKeys
    url: string
    title?: string
    description?: string
    quote?: string
    hashtags?: string
    twitterUser?: string
    media?: string
    tag?: string
    popup?: { width: number; height: number }
    networks?: { [socialNetwork: string]: string }
  }>(),
  {
    title: '',
    description: '',
    quote: '',
    hashtags: '',
    twitterUser: '',
    media: '',
    tag: 'a',
    popup: () => ({ width: 600, height: 400 }),
    networks: () => ({}),
  },
)
const networks = computed(() => ({ ...AvailableNetworks, ...props.networks }))
const key = computed(() => props.network.toLowerCase() as NetworkLowerKeys)

if (!networks.value[key.value]) {
  throw new Error('Network ' + key.value + ' does not exist')
}

let popupWindow: Window | null

const emit = defineEmits<{
  open: [NetworkLowerKeys, string]
  close: [NetworkLowerKeys, string]
  change: [NetworkLowerKeys, string]
}>()

const popupTop = ref(0)
const popupLeft = ref(0)
const popupInterval = ref<ReturnType<typeof setInterval>>()

const rawLink = computed(() => {
  const ua = navigator.userAgent.toLowerCase()

  /**
   * On IOS, SMS sharing link need a special formatting
   * Source: https://weblog.west-wind.com/posts/2013/Oct/09/Prefilling-an-SMS-on-Mobile-Devices-with-the-sms-Uri-Scheme#Body-only
   */
  if (
    key.value === 'sms' &&
    (ua.indexOf('iphone') > -1 || ua.indexOf('ipad') > -1)
  ) {
    return networks.value[key.value].replace(':?', ':&')
  }

  return networks.value[key.value]
})

const encodedHashtags = () => {
  if (key.value === 'facebook' && props.hashtags.length) {
    return '%23' + props.hashtags.split(',')[0]
  }
  return props.hashtags
}

const shareLink = computed(() => {
  let link = rawLink.value

  if (key.value === 'twitter') {
    if (!props.hashtags.length) {
      link = link.replace('&hashtags=@h', '')
    }

    if (!props.twitterUser.length) {
      link = link.replace('@tu', '')
    }
  }

  return link
    .replace(/@tu/g, '&via=' + encodeURIComponent(props.twitterUser))
    .replace(/@u/g, encodeURIComponent(props.url))
    .replace(/@t/g, encodeURIComponent(props.title))
    .replace(/@d/g, encodeURIComponent(props.description))
    .replace(/@q/g, encodeURIComponent(props.quote))
    .replace(/@h/g, encodedHashtags)
    .replace(/@m/g, encodeURIComponent(props.media))
})

const resizePopup = () => {
  if (!window) return
  const width =
    window.innerWidth ?? document.documentElement.clientWidth ?? window.screenX
  const height =
    window.innerHeight ??
    document.documentElement.clientHeight ??
    window.screenY
  const systemZoom = width / window.screen.availWidth

  popupLeft.value =
    (width - props.popup.width) / 2 / systemZoom +
    (window.screenLeft ?? window.screenX)

  popupTop.value =
    (height - props.popup.height) / 2 / systemZoom +
    (window.screenTop ?? window.screenY)
}

/**
 * Shares URL in specified network.
 */
const share = () => {
  resizePopup()

  // If a popup window already exist, we close it and trigger a change event.
  if (popupWindow && popupInterval.value) {
    clearInterval(popupInterval.value)

    popupWindow.close()
    emit('change', key.value, props.url)
  }

  if (window) {
    const target = `sharer-${key.value}`
    const features = `popup=true,height=${props.popup.height},width=${props.popup.width},left=${popupLeft.value},top=${popupTop.value},screenX=${popupLeft.value},screenY=${popupTop.value}`

    popupWindow = window.open(shareLink.value, target, features)
  }

  if (!popupWindow) {
    return
  }

  popupWindow.focus()

  popupInterval.value = setInterval(() => {
    /* 
    This can't work with cross-origin windows because the closed property 
    is not accessible and always equals true.
    The close event is triggered right after the window is opened.
    */
    if (!popupWindow || popupWindow.closed) {
      clearInterval(popupInterval.value)
      popupWindow = null
      emit('close', key.value, props.url)
    }
  }, 500)

  emit('open', key.value, props.url)
}

const touch = () => {
  if (window) {
    window.open(shareLink.value, '_blank')
    emit('open', key.value, props.url)
  }
}

const onClick = () => {
  return rawLink.value.substring(0, 4) === 'http' ? share() : touch()
}
</script>
